/* Result panel pieces: RangeNumber (count-up + flip), Delta, Distribution, RecRow */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from './icons.jsx';

/* Animate a number from prev → next over duration with easing-out */
function useCountUp(target, duration = 500) {
  const [value, setValue] = useState(target);
  const fromRef = useRef(target);
  const startRef = useRef(0);
  const rafRef = useRef(0);
  const targetRef = useRef(target);

  useEffect(() => {
    if (target === targetRef.current) return;
    fromRef.current = value;
    targetRef.current = target;
    startRef.current = performance.now();
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) { setValue(target); return; }
    const tick = (now) => {
      const t = Math.min(1, (now - startRef.current) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      const next = fromRef.current + (target - fromRef.current) * eased;
      setValue(next);
      if (t < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [target]); // eslint-disable-line react-hooks/exhaustive-deps

  return value;
}

export function fmtThousands(n) {
  return Math.round(n).toLocaleString('ru-RU').replace(/,/g, ' ');
}

export function RangeNumber({ min, max }) {
  const minV = useCountUp(min);
  const maxV = useCountUp(max);
  return (
    <div className="t-display t-tnum" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: 12 }}>
      <span style={{ fontSize: 22, lineHeight: 1, fontWeight: 500, color: 'var(--t-text-muted)' }}>от</span>
      <span>{fmtThousands(minV)}</span>
      <span style={{ fontSize: 22, lineHeight: 1, fontWeight: 500, color: 'var(--t-text-muted)' }}>до</span>
      <span>{fmtThousands(maxV)} ₽</span>
    </div>
  );
}

export function Delta({ value, animKey }) {
  if (value === 0 || value == null) {
    return (
      <span className="delta is-same">
        <Icon name="arrow-right" size={14} stroke={2.4} />
        Без изменений к прошлому расчёту
      </span>
    );
  }
  const up = value > 0;
  return (
    <span
      className={'delta ' + (up ? '' : 'is-down')}
      key={animKey}
      style={{ animation: 'slideUpDelta 360ms var(--t-ease)' }}
    >
      <Icon name={up ? 'arrow-up' : 'arrow-down'} size={14} stroke={2.4} />
      {up ? '+' : '−'}{fmtThousands(Math.abs(value))} ₽ к прошлому расчёту
    </span>
  );
}

export function Distribution({ min, max, mid }) {
  const lo = 60_000, hi = 500_000;
  const N = 28;
  const bars = useMemo(() => {
    const arr = [];
    for (let i = 0; i < N; i++) {
      const x = lo + ((hi - lo) * i) / (N - 1);
      const peak = 200_000;
      const sigma = 110_000;
      const y = Math.exp(-Math.pow((x - peak) / sigma, 2));
      arr.push({ x, y });
    }
    return arr;
  }, []);
  const ymax = Math.max(...bars.map((b) => b.y));
  return (
    <div className="dist" aria-label="Распределение зарплат на рынке">
      <div className="dist-bars">
        {bars.map((b, i) => {
          const inRange = b.x >= min && b.x <= max;
          const isMid = Math.abs(b.x - mid) < (hi - lo) / N / 1.6;
          const h = Math.max(8, Math.round((b.y / ymax) * 40));
          return (
            <div
              key={i}
              className={(isMid ? 'is-current' : inRange ? 'is-in' : '')}
              style={{ height: h }}
            />
          );
        })}
      </div>
    </div>
  );
}

export function RecRow({ rec, onClick, isActive }) {
  return (
    <div
      className={'rec' + (isActive ? ' is-active' : '')}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter') onClick(); }}
    >
      <div className="rec-icon"><Icon name={rec.icon} size={18} /></div>
      <div className="rec-body">
        <div className="rec-title">{rec.title}</div>
        {rec.sub ? <div className="rec-sub">{rec.sub}</div> : null}
      </div>
    </div>
  );
}
